import { useEffect, useMemo, useRef, type RefObject } from 'react';
import type { StationMeta } from '@tideprecision/shared';
import {
  findWindHourByMinute,
  hasSparseIntervals,
  formatFetchedAt,
  formatWindDirection,
  windSpeedTextClass,
  type WindForecastResult,
} from '../lib/wind.js';
import type { WindForecastHour } from '../lib/windTypes.js';
import { todayJst } from '../state/store.js';

interface Props {
  station: StationMeta;
  date: string;
  result: WindForecastResult | null;
  rows: WindForecastHour[];
  isLoading: boolean;
  error: string | null;
  onRefresh: () => void;
}

export function WindForecastPanel({
  station,
  date,
  result,
  rows,
  isLoading,
  error,
  onRefresh,
}: Props): JSX.Element {
  const scrollBoxRef = useRef<HTMLDivElement | null>(null);
  const activeRowRef = useRef<HTMLTableRowElement | null>(null);

  const currentWindTime = useMemo(() => {
    if (date !== todayJst()) return null;
    const jstNow = new Date(Date.now() + 9 * 60 * 60 * 1000);
    const minute = jstNow.getUTCHours() * 60 + jstNow.getUTCMinutes();
    return findWindHourByMinute(rows, minute)?.time ?? null;
  }, [date, rows]);

  useEffect(() => {
    if (currentWindTime === null || rows.length === 0) return;
    const frame = window.requestAnimationFrame(() => {
      const box = scrollBoxRef.current;
      const row = activeRowRef.current;
      if (!box || !row) return;
      box.scrollTop = row.offsetTop - box.clientHeight / 2 + row.clientHeight / 2;
    });
    return () => window.cancelAnimationFrame(frame);
  }, [currentWindTime, rows.length, station.id]);

  return (
    <section className="rounded bg-slate-800/60 border border-slate-700/40 p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs text-slate-400">風予報</div>
          <div className="text-sm font-bold text-slate-100">
            {station.name}
            <span className="ml-2 text-xs font-normal text-slate-400">
              MET Norway
            </span>
          </div>
          {result && (
            <div className="mt-1 text-[11px] text-slate-500">
              最終取得 {formatFetchedAt(result.forecast.fetchedAt)}
              {result.fromCache && !result.stale ? ' / キャッシュ' : ''}
              {result.stale ? ' / 古い予報' : ''}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={onRefresh}
          disabled={isLoading}
          className="px-3 py-1 rounded bg-slate-700 hover:bg-slate-600 disabled:opacity-50 text-sm shrink-0"
        >
          {isLoading ? '更新中…' : '更新'}
        </button>
      </div>

      {error && !result && (
        <div className="rounded border border-rose-900/50 bg-rose-950/30 px-3 py-2 text-sm text-rose-200">
          風予報を取得できませんでした
        </div>
      )}

      {result && rows.length === 0 && (
        <div className="rounded border border-slate-700/60 bg-slate-900/40 px-3 py-3 text-sm text-slate-400">
          この日の風予報は取得範囲外です
        </div>
      )}

      {hasSparseIntervals(rows) && (
        <div className="text-[11px] text-slate-500">
          この日は6時間ごとの予報です（MET Norway の提供粒度）
        </div>
      )}

      {rows.length > 0 && (
        <div ref={scrollBoxRef} className="max-h-96 overflow-y-auto rounded border border-slate-700/50">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-slate-900 text-xs text-slate-400">
              <tr>
                <th className="px-3 py-2 text-left font-semibold">時刻</th>
                <th className="px-3 py-2 text-left font-semibold">風向</th>
                <th className="px-3 py-2 text-right font-semibold">風速</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {rows.map((row) => (
                <WindRow
                  key={row.time}
                  row={row}
                  active={row.time === currentWindTime}
                  rowRef={
                    row.time === currentWindTime
                      ? activeRowRef
                      : null
                  }
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-[11px] text-slate-500">
        風予報は数値予報モデルによる予測のため、実況や他の予報サービスと異なる場合があります。安全に関わる判断には、気象庁の公式情報もあわせて確認してください。
      </p>
    </section>
  );
}

function WindRow({
  row,
  active,
  rowRef,
}: {
  row: WindForecastHour;
  active: boolean;
  rowRef: RefObject<HTMLTableRowElement> | null;
}): JSX.Element {
  const direction = formatWindDirection(row.windDirectionDeg);
  const speedClass = windSpeedTextClass(row.windSpeedMs);
  return (
    <tr ref={rowRef ?? undefined} className={active ? 'bg-sky-950/45' : 'bg-slate-900/20'}>
      <td className="px-3 py-2 font-mono text-slate-200">{row.time.slice(11, 16)}</td>
      <td className="px-3 py-2">
        <span
          className="inline-flex items-center gap-2 text-slate-100"
          aria-label={`${direction.compass}から吹く風`}
        >
          <span className={`text-lg leading-none ${speedClass}`}>{direction.arrow}</span>
          <span>{direction.compass}</span>
        </span>
      </td>
      <td className={`px-3 py-2 text-right font-mono font-bold ${speedClass}`}>
        {row.windSpeedMs.toFixed(1)}
        <span className="ml-1 text-xs text-slate-500">m/s</span>
      </td>
    </tr>
  );
}
